import React, { useEffect, useState } from 'react';
import { ItemScheme } from '../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../Enums/MagicField.enum';
import { getMomentDate } from '../../../../Utils/dates.util';
import { useAppContext } from '../../../../Services/AppContext';
import { useFormContext } from 'react-hook-form';
import TextAreaField from '../../../../Components/Form/components/TextAreaField';
import Loading from '../../../../Components/Loading';
import SubmitButton from '../../../../Components/Button/SubmitButton';
// @ts-ignore
import styles from './MagicFormPlainTextViewer.module.scss';
import { PlainTextItemConditionRule } from '../../Enums/PlainTextItemConditionRule.enum';

interface PlainTextTemplateItemCondition {
	key: string;
	value?: string | boolean;
	rule?: PlainTextItemConditionRule;
}

export interface PlainTextTemplateItem {
	text: string;
	conditions?: PlainTextTemplateItemCondition[];
	children?: PlainTextTemplateItem[];
}

interface Props {
	formScheme: ItemScheme[];
	template: PlainTextTemplateItem[];
	onSubmit: (values: any) => void;
}

const MagicFormPlainTextViewer: React.FC<Props> = ({
	formScheme,
	template,
	onSubmit,
}) => {
	const { dateFormatMoment } = useAppContext();
	const [isLoading, setIsLoading] = useState(true);
	const { getValues, setValue } = useFormContext();
	const values = getValues();

	const findItemByKey = (
		items: ItemScheme[],
		targetKey: string,
	): ItemScheme | undefined => {
		for (const item of items) {
			if (item.key === targetKey) {
				return item;
			}
			if (item.children) {
				const found = findItemByKey(item.children as ItemScheme[], targetKey);
				if (found) {
					return found;
				}
			}
		}
		return undefined;
	};

	const getItemDisplayableValue = (
		item: ItemScheme,
		value: string,
	): string | undefined => {
		if (!value) return '';

		switch (item.type) {
			case MagicField.DATEPICKER:
				return getMomentDate(value).format(dateFormatMoment);
			case MagicField.CHECKBOX:
				return item.title;
			case MagicField.SELECT:
			case MagicField.RADIOLIST:
				return (
					item.children?.find(child => child.key === value)?.title ?? undefined
				);
			default:
				return value;
		}
	};

	const checkConditions = (
		conditions: PlainTextTemplateItemCondition[],
	): boolean => {
		return conditions.every(
			({
				key,
				value: conditionValue,
				rule = PlainTextItemConditionRule.EQUAL,
			}) => {
				const currentValue = values[key];

				switch (rule) {
					case PlainTextItemConditionRule.NOT_EQUAL:
						return currentValue !== conditionValue;

					case PlainTextItemConditionRule.FILLED:
						return (
							currentValue !== undefined &&
							currentValue !== '' &&
							!(
								typeof currentValue === 'object' &&
								currentValue !== null &&
								Object.keys(currentValue).length === 0
							)
						);

					case PlainTextItemConditionRule.EQUAL:
					default:
						return currentValue === conditionValue;
				}
			},
		);
	};

	const generateTemplateString = (
		template: PlainTextTemplateItem[],
		values: Record<string, any>,
		spacer: string,
	): string => {
		const resultParts: string[] = [];

		template.forEach(item => {
			const conditions = item.conditions;
			if (conditions && !checkConditions(conditions)) {
				return;
			}

			let itemText = item.text;

			if (item.children) {
				const childString = generateTemplateString(item.children, values, ', ');
				if (childString) {
					itemText = [itemText, childString].filter(Boolean).join(spacer);
				}
			}

			if (itemText) {
				resultParts.push(itemText);
			}
		});

		return resultParts.join(spacer);
	};

	const fillTemplate = (
		template: PlainTextTemplateItem[],
		values: Record<string, any>,
		formScheme: ItemScheme[],
	): string => {
		const templateInString = generateTemplateString(template, values, '');
		return templateInString.replace(/{{(.*?)}}/g, (_, key) => {
			const trimmedKey = key.trim();
			const item = findItemByKey(formScheme, trimmedKey);

			if (!item) return '';

			const rawValue = getItemDisplayableValue(item, values[trimmedKey]);

			if (rawValue === undefined || rawValue === null) return '';

			return String(rawValue);
		});
	};

	useEffect(() => {
		if (isLoading) {
			const value = fillTemplate(template, values, formScheme);
			if (value) {
				setValue('plainText', value);
				setTimeout(() => {
					setIsLoading(false);
				}, 500);
			}
		}
	}, [template, values, formScheme]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<TextAreaField
				name='plainText'
				rows={20}
				style={{ whiteSpace: 'pre-wrap' }}
			/>
			<SubmitButton onSubmit={onSubmit} title='base.shared.save' />
		</div>
	);
};

export default MagicFormPlainTextViewer;
